import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";

/**
 * Iter 138 — Shared dashboard tabs (player + streamer).
 *
 * Props:
 *   tabs:  [{id, label, badgeKey?, node}] — badgeKey references a numeric
 *          field returned by GET /me/pending-actions. Missing field or 0 →
 *          NO badge. Only positive integers surface as a gold pill.
 *   defaultId?: string
 *   testidPrefix: e.g. "player-tabs" / "streamer-tabs"
 *
 * Mobile: horizontally-scrollable tab row (overflow-x-auto). Content stacks.
 * Refresh helper: exposes `refreshCounts` on window under a namespaced key
 * so any deeply-nested confirm / re-submit / mark-paid can call
 *   window.__vgRefreshPendingActions?.()
 * after mutating an action, without prop-drilling.
 */
export default function DashboardTabs({ tabs, defaultId, testidPrefix = "dashboard-tabs" }) {
  const [active, setActive] = useState(defaultId || tabs[0]?.id);
  const [counts, setCounts] = useState({});
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/me/pending-actions");
      setCounts(data || {});
    } catch {
      setCounts({});
    }
  }, []);
  useEffect(() => {
    load();
    window.__vgRefreshPendingActions = load;
    return () => { if (window.__vgRefreshPendingActions === load) delete window.__vgRefreshPendingActions; };
  }, [load]);

  return (
    <div className="w-full" data-testid={testidPrefix}>
      <div className="border-b border-white/10 overflow-x-auto">
        <div className="flex items-center gap-1 min-w-max" role="tablist">
          {tabs.map((t) => {
            const isSel = active === t.id;
            const rawBadge = t.badgeKey ? counts[t.badgeKey] : 0;
            const badge = Number(rawBadge) || 0;
            return (
              <button
                key={t.id}
                role="tab"
                data-testid={`${testidPrefix}-tab-${t.id}`}
                aria-selected={isSel}
                onClick={() => setActive(t.id)}
                className={`relative px-4 h-11 font-mono text-[11px] uppercase tracking-widest transition-colors whitespace-nowrap ${
                  isSel
                    ? "text-[#E8ECF4] border-b-2 border-[#2DD4A7] -mb-px"
                    : "text-[#8B97AE] hover:text-[#E8ECF4]"
                }`}
              >
                {t.label}
                {badge > 0 && (
                  <span
                    data-testid={`${testidPrefix}-badge-${t.id}`}
                    className="ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-[#F5B841] text-[#04150F] text-[10px] font-bold tabular-nums"
                  >
                    {badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
      {tabs.map((t) => (
        <section
          key={t.id}
          role="tabpanel"
          hidden={active !== t.id}
          data-testid={`${testidPrefix}-panel-${t.id}`}
          className="pt-6 space-y-6"
        >
          {t.node}
        </section>
      ))}
    </div>
  );
}
